// ~/composables/cookies/useCookieConsent.ts
import { useCookie, useRuntimeConfig } from 'nuxt/app';
import { ref } from 'vue';

export type ConsentType = 'all' | null;

export const useCookieConsent = () => {
  const cookieName = 'cookie-consent';
  const consentValue = ref<ConsentType>(null);
  
  /**
   * Get current cookie consent status
   */
  const getConsent = async (): Promise<ConsentType> => {
    if (process.server) return null;
    
    const cookieValue = useCookie<ConsentType>(cookieName).value;
    consentValue.value = cookieValue || null;
    return consentValue.value;
  };
  
  /**
   * Set cookie consent
   * @param consent - Type of consent to set
   */
  const setConsent = async (consent: ConsentType): Promise<void> => {
    if (process.server) return;
    
    const config = useRuntimeConfig();
    const options = {
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year
      path: '/',
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'strict' as const
    };
    
    const cookie = useCookie(cookieName, options);
    cookie.value = consent;
    consentValue.value = consent;
    
    // Apply consent behavior
    if (consent === 'all') {
      // Logic to enable marketing/analytics cookies could be added here
      // e.g. initialize Google Analytics, Facebook Pixel, etc.
    }
  };
  
  /**
   * Check if user has given full consent
   */
  const hasFullConsent = async (): Promise<boolean> => {
    const consent = await getConsent();
    return consent === 'all';
  };

  return {
    getConsent,
    setConsent,
    hasFullConsent
  };
};